// Copyright ©️ 2026 eVolve MEP, LLC

import './nav.css';

import { useEffect, useMemo, useState } from 'react';

import { ActionIcon, Flex } from '@mantine/core';

import {
  FABRICATION_SUB_NAV,
  GLOBAL_NAVIGATION_MENU,
  isNavActionOption,
  type NavOption,
  type NavRouteOption,
} from 'app/Layout/NavBar/globalNavigationMenu';
import { NavBarAdSpot } from 'app/Layout/NavBar/NavBarContent/NavBarAdSpot';
import { NavBarBrand } from 'app/Layout/NavBar/NavBarContent/NavBarBrand';
import { NavBarFooter, type NavbarFooterItemKey } from 'app/Layout/NavBar/NavBarContent/NavBarFooter';
import { NavBarSection } from 'app/Layout/NavBar/NavBarContent/NavBarSection';
import { NavPanes } from 'app/Layout/NavBar/NavBarContent/NavPanes';
import { SubNavBackButton } from 'app/Layout/NavBar/NavBarContent/SubNavBackButton';
import { useCanAccessRoute } from 'app/Layout/NavBar/NavBarContent/useCanAccessRoute';
import { useFabricationSubNav } from 'app/Layout/NavBar/NavBarContent/useFabricationSubNav';
import {
  navbarAnimationDuration,
  navbarAnimationTimingFunction,
  navbarTransitionStyle,
  useAppShell,
} from 'app/Layout/NavBar/useAppShell';
import { ALL_MODULE_ROUTES } from 'app/ModuleRoutes';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isKeyOf } from 'helpers/isKeyOf';
import { isNotNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { WithColorScheme } from 'styles/theme/ColorScheme';

import styledNavLinkClasses from './StyledNavLink.module.css';

export const NavBarContent = () => {
  const { setNavbarExpanded, navbarExpanded, navbarWidth } = useAppShell();
  const isMobile = useIsMobile();
  const canAccessRoute = useCanAccessRoute();
  const hasFabricationFullAccess = useHasAccess('fabrication', 'write');

  const { pushed, push, pop } = useFabricationSubNav();

  const items = useMemo(() => {
    const canShowRouteOption = ({ route }: NavRouteOption) =>
      canAccessRoute(route) && (!isMobile || isKeyOf('mobile', ALL_MODULE_ROUTES[route]));
    const canShowOption = (option: NavOption) =>
      isNavActionOption(option) ? hasFabricationFullAccess : canShowRouteOption(option);
    return {
      main: GLOBAL_NAVIGATION_MENU.map((item) => ({
        ...item,
        options: item.options.filter(canShowOption),
      })).filter((item) => item.options.length > 0),
      fabrication: { ...FABRICATION_SUB_NAV, options: FABRICATION_SUB_NAV.options.filter(canShowOption) },
    };
  }, [canAccessRoute, hasFabricationFullAccess, isMobile]);

  // To prevent menus from hovering over each other, the menus share a state here so only one can open at a time.
  // The footer has slightly different behavior. It's always a Menu even when the navbar is open.
  const [activePopover, setActivePopover] = useState<
    | {
        location: 'navbar';
        value: (typeof GLOBAL_NAVIGATION_MENU)[number]['label'] | (typeof FABRICATION_SUB_NAV)['label'];
      }
    | { location: 'footer'; value: NavbarFooterItemKey }
    | null
  >(null);
  // If the navbar is opened and there's a non-footer menu open, close it automatically.
  useEffect(() => {
    if (navbarExpanded) {
      setActivePopover((s) => (s?.location === 'navbar' ? null : s));
    }
  }, [navbarExpanded]);

  return (
    <WithColorScheme
      colorScheme="dark"
      style={{
        width: navbarWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 8,
        transition: navbarTransitionStyle,
      }}
    >
      <Flex
        direction="column"
        flex="1 0 0"
        gap="xs"
        style={{ transition: navbarTransitionStyle }}
        onKeyDown={(event) => {
          // Portalled popovers bubble here too, so only pop for focus that is inside the nav's own DOM.
          if (
            event.key === 'Escape' &&
            pushed &&
            event.target instanceof Node &&
            event.currentTarget.contains(event.target)
          ) {
            pop();
          }
        }}
      >
        {/* On mobile the logo already lives in the always-visible header, so it's omitted here. */}
        {!isMobile && (
          <>
            <Flex
              justify="space-between"
              align="center"
              ml="xs"
              mt="sm"
              h={40}
              opacity={navbarExpanded ? 1 : 0}
              style={{ transition: navbarTransitionStyle }}
            >
              <NavBarBrand navbarExpanded={navbarExpanded} pushed={pushed} onBack={pop} />
            </Flex>
            <ActionIcon
              className={styledNavLinkClasses.navLink}
              size="input-sm"
              onClick={() => setNavbarExpanded(!navbarExpanded)}
              variant="transparent"
              style={{
                position: 'absolute',
                top: 22,
                left: navbarExpanded ? navbarWidth - 40 : navbarWidth / 2 - 12,
                transition: `left ${navbarAnimationDuration}ms ${navbarAnimationTimingFunction}`,
              }}
            >
              <EvolveIcon icon="NavBarMenu" />
            </ActionIcon>
          </>
        )}

        <NavPanes
          navbarExpanded={navbarExpanded}
          pushed={pushed}
          mainPane={items.main.map((item) => (
            <NavBarSection
              key={item.label}
              navData={item}
              navbarExpanded={navbarExpanded}
              popoverOpened={activePopover?.location === 'navbar' && activePopover.value === item.label}
              onPopoverChanged={(popoverOpened) =>
                setActivePopover(popoverOpened ? { location: 'navbar', value: item.label } : null)
              }
              onOptionClick={(option) => {
                if (!isNavActionOption(option) && option.route === '/fabrication') push();
              }}
            />
          ))}
          subPane={
            <>
              {/* Without the top row (mobile) or room for its label (collapsed), Back leads the sub pane instead. */}
              {(isMobile || !navbarExpanded) && <SubNavBackButton onClick={pop} iconOnly={!navbarExpanded} />}
              <NavBarSection
                navData={items.fabrication}
                navbarExpanded={navbarExpanded}
                collapsible={false}
                popoverOpened={activePopover?.location === 'navbar' && activePopover.value === items.fabrication.label}
                onPopoverChanged={(popoverOpened) =>
                  setActivePopover(popoverOpened ? { location: 'navbar', value: items.fabrication.label } : null)
                }
              />
            </>
          }
        />
      </Flex>

      <NavBarAdSpot />
      <NavBarFooter
        activePopover={activePopover?.location === 'footer' ? activePopover.value : null}
        onPopoverChanged={(key) => setActivePopover(isNotNil(key) ? { location: 'footer', value: key } : null)}
      />
    </WithColorScheme>
  );
};
